import { RequiredProperty } from 'dashboard/models/check/government-id';
import {
  ComparedAttribute,
  ComparisonMethodType,
  IdCompareCheckConfig,
  isIdCompareCheck,
  isMatchGroupCheck,
  isMatchRequirementsCheck,
  MatchGroupsCheckConfig,
  MatchRequirement,
  MatchRequirementsCheckConfig,
  RequiredExtraction,
} from 'dashboard/models/verification-check';
import {
  ExpirationCheckConfig,
  isExpirationCheck,
} from 'dashboard/models/verification-check-config/id-expiration-detection';
import {
  ExtractionCheckConfig,
  isExtractionCheck,
} from 'dashboard/models/verification-check-config/id-extracted-properties-detection';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import { VerificationStatus } from 'lib/verification';
import { CheckName } from 'lib/verificationCheck';

import { IdCompareOption } from '../../../models/verification-template-version';

type StubDefaultCheckParams = {
  check: CheckName;
  required?: boolean;
  enabled?: boolean;
  overrideAttributes?: ComparedAttribute[] | RequiredProperty[];
  checkRequirements?: (MatchRequirement | RequiredExtraction)[];
};

/**
 * Provides check config stub for checks that were not
 * included in the default set when the verification template version
 * was created on the server.
 */
export const stubDefaultCheck = ({
  check,
  required,
  enabled,
  overrideAttributes = [],
  checkRequirements = [],
}: StubDefaultCheckParams): VerificationCheckConfig => {
  const defaultCheck: VerificationCheckConfig = {
    check: check,
    'on-failed': required ? VerificationStatus.Failed : null,
    'toggle-option': enabled ? 'enabled' : 'disabled',
  };

  if (isExtractionCheck(defaultCheck)) {
    return {
      ...defaultCheck,
      'required-properties': [
        {
          'id-class': 'default',
          'country-code': 'default',
          properties: overrideAttributes as RequiredProperty[],
        },
      ],
    } as ExtractionCheckConfig;
  } else if (isExpirationCheck(defaultCheck)) {
    return {
      ...defaultCheck,
      'expiration-thresholds': [
        {
          'country-code': 'default',
          'id-class': 'default',
          'expiration-threshold-days': 30,
          subdivision: 'default',
        },
      ],
    } as ExpirationCheckConfig;
  } else if (isIdCompareCheck(defaultCheck)) {
    return {
      ...defaultCheck,
      'id-compare-option': IdCompareOption.Last,
    } as IdCompareCheckConfig;
  } else if (isMatchGroupCheck(defaultCheck)) {
    return {
      ...defaultCheck,
      'match-groups': [
        {
          description: 'Name and Birthdate',
          name: 'name_and_birthdate',
          'country-code': 'US',
          'match-requirements': [
            {
              description: 'First Name',
              name: ComparedAttribute.NameFirst,
              'attributes-to-compare': [ComparedAttribute.NameFirst],
              'normalization-methods': [],
              'comparison-methods': [
                {
                  type: ComparisonMethodType.StringDifference,
                  'partial-match-threshold': 1,
                  'match-level-minimum': 'partial',
                },
              ],
            },
            {
              description: 'Birthdate',
              name: ComparedAttribute.Birthdate,
              'attributes-to-compare': [ComparedAttribute.Birthdate],
              'normalization-methods': [],
              'comparison-methods': [
                {
                  type: ComparisonMethodType.DateDifferenceDay,
                  'partial-match-threshold': 1,
                  'match-level-minimum': 'partial',
                },
              ],
            },
          ],
        },
      ],
    } as MatchGroupsCheckConfig;
  } else if (isMatchRequirementsCheck(defaultCheck)) {
    return {
      ...defaultCheck,
      'check-requirements': (checkRequirements as MatchRequirement[]) ?? [],
    } as MatchRequirementsCheckConfig;
  } else {
    return defaultCheck;
  }
};

/**
 * returns index of VerificationCheck for given check name
 */
export const indexForCheck = (name: CheckName, checks: VerificationCheckConfig[]): number => {
  return checks.findIndex((check) => check.check === name);
};
